/**
 * `defer-flowchart-server-persist` — wire schemas for server-side
 * annotation-set persistence.
 *
 * Mirrors the existing `Annotation` / `AnnotationSet` shape in
 * `packages/flow-builder/src/lib/screenshot-annotations/types.ts`
 * so the editor's data layer can round-trip through the API
 * without a transform. Both sides import these Zod schemas via
 * `@rynora/contracts` (the FE library still owns the operations
 * + canvas component; the contract package owns the wire shape).
 */
import { z } from 'zod';

export const AnnotationKindSchema = z.enum(['rect', 'arrow', 'highlight', 'text']);
export type AnnotationKind = z.infer<typeof AnnotationKindSchema>;

export const AnnotationColourSchema = z.enum(['red', 'orange', 'yellow', 'green', 'blue', 'magenta']);
export type AnnotationColour = z.infer<typeof AnnotationColourSchema>;

const AnnotationBase = z.object({
  id: z.string().min(1).max(120),
  colour: AnnotationColourSchema,
  strokeWidth: z.number().min(0).max(40),
  caption: z.string().max(280).optional(),
  createdAt: z.string(),
  /**
   * `defer-annotation-collab` — uuid of the user who authored this
   * annotation. Optional because annotations created before the
   * collab cluster shipped don't have one; the server fills it in
   * on write when the field is missing so newly-saved annotations
   * always carry an author. We keep the validator forgiving (max
   * 120 chars, no uuid refinement) so synthetic ids in fixtures and
   * older clients still round-trip.
   */
  authorUserId: z.string().min(1).max(120).optional(),
});

const Norm = z.number().min(0).max(1);

export const RectAnnotationSchema = AnnotationBase.extend({
  kind: z.literal('rect'),
  x: Norm,
  y: Norm,
  width: Norm,
  height: Norm,
});
export type RectAnnotation = z.infer<typeof RectAnnotationSchema>;

export const ArrowAnnotationSchema = AnnotationBase.extend({
  kind: z.literal('arrow'),
  x1: Norm,
  y1: Norm,
  x2: Norm,
  y2: Norm,
});
export type ArrowAnnotation = z.infer<typeof ArrowAnnotationSchema>;

export const HighlightAnnotationSchema = AnnotationBase.extend({
  kind: z.literal('highlight'),
  x: Norm,
  y: Norm,
  width: Norm,
  height: Norm,
});
export type HighlightAnnotation = z.infer<typeof HighlightAnnotationSchema>;

export const TextAnnotationSchema = AnnotationBase.extend({
  kind: z.literal('text'),
  x: Norm,
  y: Norm,
  text: z.string().min(1).max(280),
  fontSize: z.number().min(6).max(96),
});
export type TextAnnotation = z.infer<typeof TextAnnotationSchema>;

export const AnnotationSchema = z.discriminatedUnion('kind', [
  RectAnnotationSchema,
  ArrowAnnotationSchema,
  HighlightAnnotationSchema,
  TextAnnotationSchema,
]);
export type Annotation = z.infer<typeof AnnotationSchema>;

/**
 * The wrapper persisted alongside the screenshot. The frontend
 * caps `annotations.length` at 200 implicitly through the UI; the
 * server enforces it explicitly.
 */
export const AnnotationSetSchema = z.object({
  version: z.literal(1),
  screenshotId: z.string().min(1).max(300),
  imageWidth: z.number().int().min(1).max(20000),
  imageHeight: z.number().int().min(1).max(20000),
  annotations: z.array(AnnotationSchema).max(200),
  updatedAt: z.string(),
});
export type AnnotationSet = z.infer<typeof AnnotationSetSchema>;

export const PutAnnotationSetRequestSchema = z.object({
  set: AnnotationSetSchema,
  /**
   * `defer-annotation-collab` — optimistic-concurrency token. When
   * provided, the server compares this against the row's current
   * `updated_at`. If they differ the PUT is rejected with
   * `ANNOTATION_SET_CONFLICT`. Clients pass the `updatedAt` they
   * received from the most recent GET.
   *
   * Optional so older clients (and the initial save of a brand-new
   * set, where the caller has no prior `updatedAt`) keep working.
   */
  expectedUpdatedAt: z.string().optional(),
});
export type PutAnnotationSetRequest = z.infer<typeof PutAnnotationSetRequestSchema>;

/**
 * `defer-annotation-collab` — single contributor entry returned
 * alongside an `AnnotationSet`. Computed by the server by joining
 * `annotation_sets.payload->'annotations'[*].authorUserId` against
 * `users`. `displayName` is best-effort (`name` falling back to
 * `email`); `count` is the number of annotations attributed to the
 * user in the current set.
 */
export const AnnotationContributorSchema = z.object({
  userId: z.string(),
  displayName: z.string(),
  count: z.number().int().nonnegative(),
  /** Latest `createdAt` for this user's annotations in this set. */
  lastEditedAt: z.string(),
});
export type AnnotationContributor = z.infer<typeof AnnotationContributorSchema>;

/**
 * `defer-annotation-collab` — wire envelope for `GET
 * /annotation-sets/:screenshotId`. The route returned `{ set,
 * updatedAt }` before this cluster; we add `contributors` while
 * keeping the schema backwards-compatible (callers that ignore the
 * field continue to work).
 */
export const GetAnnotationSetResponseSchema = z.object({
  set: AnnotationSetSchema,
  updatedAt: z.string(),
  contributors: z.array(AnnotationContributorSchema).default([]),
});
export type GetAnnotationSetResponse = z.infer<typeof GetAnnotationSetResponseSchema>;

/**
 * `defer-annotation-collab` — body returned with HTTP 409 when a
 * PUT loses an optimistic-concurrency race. Includes the latest
 * stored set + contributors so the editor can show "you and Bob
 * both saved — keep yours, discard, or merge".
 */
export const AnnotationSetConflictResponseSchema = z.object({
  code: z.literal('ANNOTATION_SET_CONFLICT'),
  message: z.string(),
  latest: GetAnnotationSetResponseSchema,
});
export type AnnotationSetConflictResponse = z.infer<typeof AnnotationSetConflictResponseSchema>;

export const AnnotationSetRowSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  screenshotId: z.string(),
  payload: AnnotationSetSchema,
  updatedByUserId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AnnotationSetRow = z.infer<typeof AnnotationSetRowSchema>;

/**
 * Summary entry used by run history to render "this screenshot has
 * N annotations" badges without fetching the full payload.
 */
export const AnnotationSetSummarySchema = z.object({
  screenshotId: z.string(),
  count: z.number().int().nonnegative(),
  updatedAt: z.string(),
});
export type AnnotationSetSummary = z.infer<typeof AnnotationSetSummarySchema>;

export const ListAnnotationSetsResponseSchema = z.object({
  summaries: z.array(AnnotationSetSummarySchema),
});
export type ListAnnotationSetsResponse = z.infer<typeof ListAnnotationSetsResponseSchema>;
